import { COLORS, MAZE } from '../game/constants';
import type { MazeLayout } from '../game/mazeLayout';
import { SCORE_ROW, slotsLeftToRight } from '../game/scoreRow';
import { slotTierColor } from '../game/constants';

/**
 * ============================================================================
 * THE TOP-DOWN DEBUG VIEW
 * ============================================================================
 *
 * A plan view of the machine drawn from the COLLISION DATA, not from the
 * rendered scene. It answers the question the rendered scene cannot: "where are
 * the walls the ball actually hits, and is there a gap wide enough to get
 * through?"
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 * ---------------------------------------------------------------------------
 * A ball that will not go down a passage and a ball that is not supposed to go
 * down it look identical on screen. The rendered board is a lit, shaded,
 * semi-transparent object seen at an angle; a 45 mm gap between two rails is
 * invisible at that scale, and a gap that is 20 mm too narrow looks exactly like
 * a gap that is fine.
 *
 * This view draws the same boxes Rapier gets, at their true positions, in the
 * board's own frame, plus the ball's own collider. A passage the ball cannot fit
 * through is then something you can see rather than something you infer from a
 * ball refusing to move.
 *
 * It also draws the scoring row - which does NOT rotate - so the two coordinate
 * systems are on one picture and the aiming problem is legible: the row is
 * fixed, the maze turns, and the ball leaves through the guard's exit window.
 *
 * ---------------------------------------------------------------------------
 * WHAT IT IS NOT
 * ---------------------------------------------------------------------------
 * It is not a renderer and it never feeds anything back. `draw()` reads the
 * layout and the ball; nothing reads the debug view. It can be wrong on screen
 * without changing a single thing about the game, which is what makes it safe to
 * leave switched on.
 *
 * Toggle with `G`, or `window.__game.setDebugView(true)`.
 */
export class DebugView {
  private readonly canvas: HTMLCanvasElement;
  private readonly context: CanvasRenderingContext2D;
  private readonly panel: HTMLDivElement;
  private shown = false;

  /** Counts from the most recent draw, so a test can prove it drew the colliders. */
  private lastDraw = { segments: 0, obstacles: 0, pockets: 0, ballDrawn: false };

  /**
   * Half-extent of the world window, in metres.
   *
   * Wide enough to hold the scoring row's end walls (1.364) and the board's plate
   * (1.25) with a little margin, so the whole aiming problem is on one picture.
   */
  private static readonly WORLD_HALF = 1.75;
  private static readonly PIXELS = 360;

  constructor(parent: HTMLElement) {
    this.panel = document.createElement('div');
    this.panel.className = 'debug-view';

    this.canvas = document.createElement('canvas');
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = DebugView.PIXELS * ratio;
    this.canvas.height = DebugView.PIXELS * ratio;
    this.canvas.style.width = `${DebugView.PIXELS}px`;
    this.canvas.style.height = `${DebugView.PIXELS}px`;

    const context = this.canvas.getContext('2d');
    if (!context) throw new Error('the debug view needs a 2D canvas context');
    this.context = context;
    this.context.scale(ratio, ratio);

    const caption = document.createElement('div');
    caption.className = 'debug-view-caption';
    caption.textContent = '俯視碰撞圖（G 切換）';

    this.panel.append(caption, this.canvas);
    parent.appendChild(this.panel);

    window.addEventListener('keydown', this.onKeyDown);
    this.setVisible(false);
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    this.panel.remove();
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.code === 'KeyG') this.setVisible(!this.shown);
  };

  get visible(): boolean {
    return this.shown;
  }

  setVisible(visible: boolean): void {
    this.shown = visible;
    this.panel.style.display = visible ? 'block' : 'none';
  }

  /** What the last `draw()` put on the canvas. */
  get stats(): { segments: number; obstacles: number; pockets: number; ballDrawn: boolean } {
    return { ...this.lastDraw };
  }

  /**
   * Redraw.
   *
   * `boardAngleRad` is the board's rotation; the maze's colliders are drawn in
   * the board's LOCAL frame and rotated here, exactly as the kinematic body
   * rotates them in the simulation. The scoring row is NOT rotated - it is fixed
   * to the cabinet - which is why it is drawn from the row's own world-space
   * numbers rather than through the same transform.
   */
  draw(input: {
    layout: MazeLayout;
    boardAngleRad: number;
    ball: { x: number; y: number } | null;
    ballColliderRadius: number;
  }): void {
    this.lastDraw = { segments: 0, obstacles: 0, pockets: 0, ballDrawn: false };
    if (!this.shown) return;

    const ctx = this.context;
    const size = DebugView.PIXELS;
    const scale = size / (2 * DebugView.WORLD_HALF);
    const toX = (x: number) => size / 2 + x * scale;
    const toY = (y: number) => size / 2 - y * scale;

    ctx.clearRect(0, 0, size, size);
    ctx.fillStyle = '#04070f';
    ctx.fillRect(0, 0, size, size);

    const angle = input.boardAngleRad;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    /** Board-local point -> screen. */
    const local = (x: number, y: number): [number, number] => [
      toX(x * cos - y * sin),
      toY(x * sin + y * cos),
    ];

    // ---- the board's outline, so the frame is legible ----------------------
    ctx.setLineDash([3, 3]);
    ctx.strokeStyle = 'rgba(120, 150, 200, 0.35)';
    ctx.lineWidth = 1;
    for (const radius of [MAZE.plateRadius, MAZE.hubRadius]) {
      ctx.beginPath();
      for (let i = 0; i <= 64; i += 1) {
        const a = (i / 64) * Math.PI * 2;
        const [px, py] = local(radius * Math.cos(a), radius * Math.sin(a));
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.stroke();
    }
    ctx.setLineDash([]);

    // ---- the guard, drawn as ARCS so the exit window is visible ------------
    ctx.lineWidth = MAZE.outerRingTube * 2 * scale;
    ctx.strokeStyle = 'rgba(53, 200, 255, 0.35)';
    for (const arc of input.layout.guardArcs) {
      ctx.beginPath();
      const steps = Math.max(2, Math.round((arc.endDeg - arc.startDeg) / 4));
      for (let i = 0; i <= steps; i += 1) {
        const a = ((arc.startDeg + ((arc.endDeg - arc.startDeg) * i) / steps) * Math.PI) / 180;
        const [px, py] = local(MAZE.outerRadius * Math.cos(a), MAZE.outerRadius * Math.sin(a));
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.stroke();
    }

    // ---- the maze colliders, one box per collider --------------------------
    const kindColours: Record<string, string> = {
      ring: '#3b82f6',
      guard: '#22d3ee',
      radialWall: '#ef4444',
      deflector: '#a855f7',
      ramp: '#f97316',
    };
    for (const segment of input.layout.segments) {
      const halfLength = segment.length / 2;
      const halfThickness = segment.thickness / 2;
      const c = Math.cos(segment.angleRad);
      const s = Math.sin(segment.angleRad);
      const corners: [number, number][] = [
        [-halfLength, -halfThickness],
        [halfLength, -halfThickness],
        [halfLength, halfThickness],
        [-halfLength, halfThickness],
      ].map(([u, v]) => local(segment.x + u * c - v * s, segment.y + u * s + v * c));

      ctx.beginPath();
      ctx.moveTo(corners[0][0], corners[0][1]);
      for (const [px, py] of corners.slice(1)) ctx.lineTo(px, py);
      ctx.closePath();
      ctx.fillStyle = kindColours[segment.kind] ?? '#888888';
      ctx.fill();
      this.lastDraw.segments += 1;
    }

    // ---- pegs --------------------------------------------------------------
    for (const obstacle of input.layout.obstacles) {
      const [px, py] = local(obstacle.x, obstacle.y);
      ctx.beginPath();
      ctx.arc(px, py, obstacle.radius * scale, 0, Math.PI * 2);
      ctx.fillStyle = '#c9d6e8';
      ctx.fill();
      this.lastDraw.obstacles += 1;
    }

    // ---- the scoring row: FIXED, in world space ---------------------------
    ctx.fillStyle = 'rgba(154, 163, 173, 0.85)';
    for (let index = 0; index <= SCORE_ROW.count; index += 1) {
      const x = -SCORE_ROW.span + SCORE_ROW.width * index;
      const left = toX(x - SCORE_ROW.divider / 2);
      const right = toX(x + SCORE_ROW.divider / 2);
      ctx.fillRect(left, toY(SCORE_ROW.mouthY), right - left, SCORE_ROW.depth * scale);
    }

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '600 9px "SF Mono", Menlo, monospace';
    for (const entry of slotsLeftToRight()) {
      const left = toX(entry.centerX - entry.halfWidth);
      const right = toX(entry.centerX + entry.halfWidth);
      const top = toY(SCORE_ROW.mouthY);
      const height = SCORE_ROW.depth * scale;

      // The pocket's footprint, tinted by tier so the value ramp reads down the
      // row the same way it does on the machine.
      const colour = `#${slotTierColor(entry.tier).toString(16).padStart(6, '0')}`;
      ctx.globalAlpha = 0.35;
      ctx.fillStyle = colour;
      ctx.fillRect(left, top, right - left, height);
      ctx.globalAlpha = 1;

      // The pocket's SCORING BOUNDARY - the exact interval `resolveSlot()` uses.
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)';
      ctx.lineWidth = 1;
      ctx.strokeRect(left + 0.5, top + 0.5, right - left - 1, height - 1);

      ctx.fillStyle = '#ffffff';
      ctx.fillText(String(entry.points), (left + right) / 2, top + height / 2);
      this.lastDraw.pockets += 1;
    }

    // ---- the ball, at its COLLIDER radius ---------------------------------
    if (input.ball) {
      const [px, py] = [toX(input.ball.x), toY(input.ball.y)];
      ctx.beginPath();
      ctx.arc(px, py, input.ballColliderRadius * scale, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.28)';
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // The ball's centre, so a ball touching a wall is distinguishable from a
      // ball overlapping one.
      ctx.beginPath();
      ctx.arc(px, py, 1.5, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      this.lastDraw.ballDrawn = true;
    }

    // ---- legend ------------------------------------------------------------
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.font = '9px "SF Mono", Menlo, monospace';
    const legend: [string, string][] = [
      ['#3b82f6', 'rail'],
      ['#22d3ee', 'guard'],
      ['#ef4444', 'radial'],
      ['#a855f7', 'deflector'],
      ['#f97316', 'ramp'],
      [COLORS.scoreSilver.toString(16), 'peg'],
    ];
    let y = 6;
    for (const [colour, label] of legend) {
      ctx.fillStyle = colour.startsWith('#') ? colour : `#${colour}`;
      ctx.fillRect(6, y + 2, 6, 6);
      ctx.fillStyle = '#c9d6e8';
      ctx.fillText(label, 16, y);
      y += 11;
    }
  }
}
